import React, {
    useEffect,
    useState
} from 'react';
import {
    getCurrentUserRole,
    getCurrentUserId
} from '../utils/authHelper';
import {formatDate} from '../utils/dateConverter';

import {
    getPublicHolidays,
    deletePublicHoliday,
    addPublicHoliday
} from "../services/PublicHolidayServices";

import {
    getAllUsers,
    deleteUser,
    changePassword,
    addUser,
    changeUserActiveState
} from "../services/UserService";

import {
    getAllWorkingTime,
    addWorkingTime,
    deleteWorkingTime
} from "../services/WorkingTimeService";

import '../styles/Settings.css';

export default function Settings() {
    const [role, setRole] = useState('');
    const [users, setUsers] = useState([]);
    const [newUser, setNewUser] = useState({
        username: '',
        name: '',
        passwordClear: '',
        role: 'user'
    });
    const [ownPassword, setOwnPassword] = useState('');
    const [adminPasswordChanges, setAdminPasswordChanges] = useState({});
    const [holidays, setHolidays] = useState([]);
    const [newHoliday, setNewHoliday] = useState({date: '', description: ''});
    const [activeTab, setActiveTab] = useState('password');
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [filterType, setFilterType] = useState('all');

    const [workingTimes, setWorkingTimes] = useState([]);
    const [newWorkingTime, setNewWorkingTime] = useState({
        userId: '',
        validFrom: '',
        validUntil: '',
        weekday: '1',
        expectedHours: '08:00'
    });
    const [selectedUserForWT, setSelectedUserForWT] = useState('all');

    useEffect(() => {
        const currentRole = getCurrentUserRole();
        setRole(currentRole);

        if (currentRole === 'admin') {
            void loadUsers();
            void loadHolidays();
            void loadWorkingTimes();
        }
    }, []);

    const loadHolidays = async () => {
        try {
            const publicHoliday = await getPublicHolidays();
            setHolidays(publicHoliday);
        } catch (err) {
            console.error("Failed to load holidays", err);
        }
    };

    const loadUsers = async () => {
        try {
            const users = await getAllUsers();
            setUsers(users);
        } catch (err) {
            console.error("Failed to load users", err);
        }
    };

    const loadWorkingTimes = async () => {
        try {
            const wts = await getAllWorkingTime();
            setWorkingTimes(wts);
        } catch (err) {
            console.error("Failed to load working times", err);
        }
    };

    const filteredHolidays = holidays
        .filter(h => new Date(h.date).getFullYear() === parseInt(filterYear))
        .filter(h => {
            const today = new Date();
            const holidayDate = new Date(h.date);
            if (filterType === 'future') return holidayDate >= today;
            if (filterType === 'past') return holidayDate < today;
            return true;
        });

    const handleDeleteUser = async (userId) => {
        if (!window.confirm("Delete User?")) return;
        try {
            await deleteUser(userId);
            await loadUsers();
        } catch (err) {
            console.error("Error deleting user", err);
            alert("Error while deleting a user");
        }
    };

    const handleToggleUserActive = async (user) => {
        const nextIsActive = !user.is_active;

        if (!window.confirm(`Benutzer "${user.username}" wirklich ${nextIsActive ? 'aktivieren' : 'deaktivieren'}?`)) return;

        try {
            await changeUserActiveState(user.id, nextIsActive);
            await loadUsers();
        } catch (err) {
            console.error("Error changing user active state", err);
            alert("Fehler beim Ändern des Aktiv-Status");
        }
    };

    const handleOwnPasswordChange = async () => {
        const userId = getCurrentUserId();
        if (!ownPassword) return alert("Please enter password");

        try {
            await changePassword(userId, ownPassword);
            setOwnPassword('');
        } catch {
            alert("Error changing password");
        }
    };

    const handleAdminPasswordChange = async (userId) => {
        const newPassword = adminPasswordChanges[userId];
        if (!newPassword) return alert("Please enter new password");

        try {
            await changePassword(userId, newPassword);
            setAdminPasswordChanges(prev => ({...prev, [userId]: ''}));
        } catch {
            alert("Error updating password");
        }
    };

    const handleCreateUser = async () => {
        if (!newUser.username || !newUser.passwordClear || !newUser.name)
            return alert("Fill all fields");

        try {
            await addUser(newUser);
            setNewUser({
                username: '',
                passwordClear: '',
                role: 'user',
                name: ''
            });
            await loadUsers();
        } catch (err) {
            alert("Error creating user");
        }
    };

    const handleAddHoliday = async () => {
        if (!newHoliday.date || !newHoliday.description)
            return alert("Fill all fields");

        try {
            await addPublicHoliday(newHoliday);
            setNewHoliday({date: '', description: ''});
            await loadHolidays();
        } catch (err) {
            alert(err.response?.data?.status || "Error adding holiday");
        }
    };

    const handleDeleteHoliday = async (id) => {
        if (!window.confirm("Delete this holiday?")) return;
        try {
            await deletePublicHoliday(id);
            await loadHolidays();
        } catch (err) {
            console.error("Error deleting holiday", err);
            alert("Error deleting holiday");
        }
    };

    const weekdayToLabel = (weekday) => {
        const labels = [
            'Sonntag',
            'Montag',
            'Dienstag',
            'Mittwoch',
            'Donnerstag',
            'Freitag',
            'Samstag'
        ];
        return labels[weekday] ?? weekday;
    };

    const findUserName = (userId) => {
        const u = users.find(u => u.id === userId);
        return u ? `${u.username} (${u.name})` : userId;
    };

    const filteredWorkingTimes = workingTimes.filter(wt =>
        selectedUserForWT === 'all' ? true : wt.userId === selectedUserForWT
    );

    const handleAddWorkingTime = async () => {
        const {userId, validFrom, validUntil, weekday, expectedHours} = newWorkingTime;

        if (!userId || !validFrom || !expectedHours) {
            return alert("Bitte Benutzer, Gültig ab und Stunden ausfüllen");
        }

        try {
            const [h, m] = expectedHours.split(':');
            const expected = `${h.padStart(2, '0')}:${(m || '00').padStart(2, '0')}:00`;

            await addWorkingTime({
                userId,
                ValidFrom: validFrom,
                ValidUntil: validUntil || null,
                Weekday: parseInt(weekday, 10),
                ExpectedHours: expected
            });

            setNewWorkingTime({
                userId: '',
                validFrom: '',
                validUntil: '',
                weekday: '1',
                expectedHours: '08:00'
            });

            await loadWorkingTimes();
        } catch (err) {
            console.error("Error adding working time", err);
            alert("Fehler beim Anlegen der Arbeitszeit-Regel");
        }
    };

    const handleDeleteWorkingTime = async (id) => {
        if (!window.confirm("Diese Arbeitszeit-Regel löschen?")) return;
        try {
            await deleteWorkingTime(id);
            await loadWorkingTimes();
        } catch (err) {
            console.error("Error deleting working time", err);
            alert("Fehler beim Löschen der Arbeitszeit-Regel");
        }
    };

    const holidayCount = filteredHolidays.length;

    const holidayCountLabel =
        filterType === 'future'
            ? 'Zukünftige Feiertage'
            : filterType === 'past'
                ? 'Vergangene Feiertage'
                : 'Feiertage';

    const holidayFilterSummary = `${holidayCountLabel} ${filterYear}: ${holidayCount}`;

    return (
        <div className="settings-container">
            <h2>Einstellungen</h2>

            <div className="internal-nav">
                <button
                    className={`internal-tab ${activeTab === 'password' ? 'active' : ''}`}
                    onClick={() => setActiveTab('password')}>
                    Passwort ändern
                </button>
                {role === 'admin' && (
                    <>
                        <button
                            className={`internal-tab ${activeTab === 'users' ? 'active' : ''}`}
                            onClick={() => setActiveTab('users')}>
                            Benutzerverwaltung
                        </button>
                        <button
                            className={`internal-tab ${activeTab === 'holidays' ? 'active' : ''}`}
                            onClick={() => setActiveTab('holidays')}>
                            Feiertage
                        </button>
                        <button
                            className={`internal-tab ${activeTab === 'working_time' ? 'active' : ''}`}
                            onClick={() => setActiveTab('working_time')}>
                            Arbeitszeit
                        </button>
                    </>
                )}
            </div>

            {activeTab === 'password' && (
                <div className="own-password-section">
                    <h3>Passwort anpassen:</h3>
                    <input
                        type="password"
                        value={ownPassword}
                        onChange={e => setOwnPassword(e.target.value)}
                        className="input"
                        placeholder="Neues Passwort"
                    />
                    <br/>
                    <button onClick={handleOwnPasswordChange} className="button">Anpassen</button>
                </div>
            )}

            {role === 'admin' && activeTab === 'working_time' && (
                <div className="admin-section">
                    <h3>Arbeitszeit-Regeln verwalten</h3>

                    <div className="two-col-grid">
                        {/*  Create */}
                        <div className="two-col-left">
                            <h4>Neue Arbeitszeit-Regel anlegen</h4>

                            <div className="workingtime-form">
                                <label>Benutzer</label>
                                <select
                                    className="input"
                                    value={newWorkingTime.userId}
                                    onChange={(e) =>
                                        setNewWorkingTime((prev) => ({...prev, userId: e.target.value}))
                                    }
                                >
                                    <option value="">– Benutzer wählen –</option>
                                    {users.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.username} ({u.name})
                                        </option>
                                    ))}
                                </select>

                                <label>Gültig ab</label>
                                <input
                                    type="date"
                                    className="input"
                                    value={newWorkingTime.validFrom}
                                    onChange={(e) =>
                                        setNewWorkingTime((prev) => ({...prev, validFrom: e.target.value}))
                                    }
                                />

                                <label>Gültig bis (optional)</label>
                                <input
                                    type="date"
                                    className="input"
                                    value={newWorkingTime.validUntil}
                                    onChange={(e) =>
                                        setNewWorkingTime((prev) => ({...prev, validUntil: e.target.value}))
                                    }
                                />

                                <label>Wochentag</label>
                                <select
                                    className="input"
                                    value={newWorkingTime.weekday}
                                    onChange={(e) =>
                                        setNewWorkingTime((prev) => ({...prev, weekday: e.target.value}))
                                    }
                                >
                                    <option value="1">Montag</option>
                                    <option value="2">Dienstag</option>
                                    <option value="3">Mittwoch</option>
                                    <option value="4">Donnerstag</option>
                                    <option value="5">Freitag</option>
                                    <option value="6">Samstag</option>
                                    <option value="0">Sonntag</option>
                                </select>

                                <label>Erwartete Stunden (HH:mm)</label>
                                <input
                                    type="time"
                                    className="input"
                                    value={newWorkingTime.expectedHours}
                                    onChange={(e) =>
                                        setNewWorkingTime((prev) => ({...prev, expectedHours: e.target.value}))
                                    }
                                />

                                <button onClick={handleAddWorkingTime} className="button" style={{marginTop: 8}}>
                                    Regel anlegen
                                </button>
                            </div>
                        </div>

                        {/* List */}
                        <div className="two-col-right">
                            <h4>Bestehende Regeln</h4>

                            <div className="table-toolbar">
                                <div>
                                    <label>Filter nach Benutzer:</label>
                                    <select
                                        className="input"
                                        value={selectedUserForWT}
                                        onChange={(e) => setSelectedUserForWT(e.target.value)}
                                    >
                                        <option value="all">Alle</option>
                                        {users.map((u) => (
                                            <option key={u.id} value={u.id}>
                                                {u.username} ({u.name})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="working-time-list">
                                <table className="table">
                                    <thead>
                                    <tr>
                                        <th>Benutzer</th>
                                        <th>Wochentag</th>
                                        <th>Gültig ab</th>
                                        <th>Gültig bis</th>
                                        <th>Erwartete Stunden</th>
                                        <th>Aktion</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredWorkingTimes.map((wt) => (
                                        <tr key={wt.id}>
                                            <td>{findUserName(wt.userId)}</td>
                                            <td>{weekdayToLabel(wt.weekday)}</td>
                                            <td>{wt.validFrom ? formatDate(wt.validFrom) : "-"}</td>
                                            <td>{wt.validUntil ? formatDate(wt.validUntil) : "-"}</td>
                                            <td>{wt.expectedHours ? wt.expectedHours.slice(0, 5) : "-"}</td>
                                            <td>
                                                <button
                                                    className="button button-danger"
                                                    onClick={() => handleDeleteWorkingTime(wt.id)}
                                                >
                                                    Löschen
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredWorkingTimes.length === 0 && (
                                        <tr>
                                            <td colSpan={6}>Keine Arbeitszeit-Regeln gefunden.</td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {role === 'admin' && activeTab === 'users' && (
                <div className="admin-section">
                    <h3>Benutzer Management</h3>

                    <div className="two-col-grid">
                        {/* Create user */}
                        <div className="two-col-left">
                            <h4>Neuer Benutzer anlegen</h4>

                            <input
                                placeholder="Benutzername"
                                value={newUser.username}
                                onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                                className="input"
                            />
                            <input
                                placeholder="Name"
                                value={newUser.name}
                                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                                className="input"
                            />
                            <input
                                placeholder="Passwort"
                                type="password"
                                value={newUser.passwordClear}
                                onChange={(e) => setNewUser({...newUser, passwordClear: e.target.value})}
                                className="input"
                            />
                            <select
                                value={newUser.role}
                                onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                                className="input"
                            >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                            </select>

                            <button onClick={handleCreateUser} className="button" style={{marginTop: 8}}>
                                Benutzer anlegen
                            </button>
                        </div>

                        {/* Users list */}
                        <div className="two-col-right">
                            <h4>Mitarbeiter</h4>

                            <table className="table">
                                <thead>
                                <tr>
                                    <th>Benutzername</th>
                                    <th>Rolle</th>
                                    <th>Status</th>
                                    <th>Passwort</th>
                                    <th>Aktion</th>
                                </tr>
                                </thead>
                                <tbody>
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td>{user.username}</td>
                                        <td>{user.role}</td>

                                        <td>
                                            {user.is_active ? 'Aktiv' : 'Inaktiv'}
                                        </td>

                                        <td>
                                            <div className="user-actions-row">
                                                <input
                                                    type="password"
                                                    value={adminPasswordChanges[user.id] || ""}
                                                    onChange={(e) =>
                                                        setAdminPasswordChanges((prev) => ({
                                                            ...prev,
                                                            [user.id]: e.target.value,
                                                        }))
                                                    }
                                                    className="input"
                                                    placeholder="Passwort"
                                                />
                                            </div>
                                        </td>

                                        <td className="user-actions-row">
                                            <button
                                                className="button"
                                                onClick={() => handleAdminPasswordChange(user.id)}>
                                                Ändern
                                            </button>

                                            <button
                                                className="button"
                                                onClick={() => handleToggleUserActive(user)}
                                                style={{
                                                    backgroundColor: user.is_active ? '#f0ad4e' : '#5cb85c'
                                                }}
                                            >
                                                {user.is_active ? 'Deaktivieren' : 'Aktivieren'}
                                            </button>

                                            <button
                                                className="button button-danger"
                                                onClick={() => handleDeleteUser(user.id)}>
                                                Löschen
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {users.length === 0 && (
                                    <tr>
                                        <td colSpan={5}>Keine Benutzer gefunden.</td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {role === 'admin' && activeTab === 'holidays' && (
                <div className="admin-section">
                    <h3>Feiertage verwalten</h3>

                    <div className="two-col-grid">
                        <div className="two-col-left">
                            <h4>Filter</h4>

                            <div className="holiday-filter">
                                <div className="form-group">
                                    <label>Jahr:</label>
                                    <input
                                        type="number"
                                        className="input"
                                        value={filterYear}
                                        onChange={(e) => setFilterYear(e.target.value)}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Typ:</label>
                                    <select
                                        className="input"
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                    >
                                        <option value="all">Alle</option>
                                        <option value="future">Zukünftig</option>
                                        <option value="past">Vergangen</option>
                                    </select>
                                </div>
                            </div>

                            <hr style={{margin: "16px 0"}}/>

                            <h4>Feiertag hinzufügen</h4>
                            <input
                                type="date"
                                value={newHoliday.date}
                                onChange={(e) =>
                                    setNewHoliday((prev) => ({...prev, date: e.target.value}))
                                }
                                className="input"
                            />
                            <input
                                type="text"
                                placeholder="Beschreibung"
                                value={newHoliday.description}
                                onChange={(e) =>
                                    setNewHoliday((prev) => ({...prev, description: e.target.value}))
                                }
                                className="input"
                            />
                            <button onClick={handleAddHoliday} className="button" style={{marginTop: 8}}>
                                Hinzufügen
                            </button>
                        </div>

                        {/* List */}
                        <div className="two-col-right">
                            <h4>Alle Feiertage</h4>

                            <div style={{marginBottom: 8, fontWeight: 600}}>
                                {holidayFilterSummary}
                            </div>

                            <div className="holiday-list">
                                <table className="table">
                                    <thead>
                                    <tr>
                                        <th>Datum</th>
                                        <th>Beschreibung</th>
                                        <th>Aktion</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredHolidays.map((h) => (
                                        <tr key={h.id}>
                                            <td>{formatDate(h.date)}</td>
                                            <td>{h.description}</td>
                                            <td>
                                                <button
                                                    onClick={() => handleDeleteHoliday(h.id)}
                                                    className="button button-danger"
                                                >
                                                    Löschen
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredHolidays.length === 0 && (
                                        <tr>
                                            <td colSpan={3}>Keine Feiertage gefunden.</td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}